import { useEffect, useState } from 'react'
import { roomsApi } from '../api'
import type { RoomMember } from '../api/types'
import { useToast } from '../context/ToastContext'
import { Button } from './ui/Button'
import { Spinner } from './ui/Spinner'

interface MembersPanelProps {
  roomId: string
  onClose: () => void
}

export function MembersPanel({ roomId, onClose }: MembersPanelProps) {
  const toast = useToast()
  const [members, setMembers] = useState<RoomMember[] | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  async function load() {
    try {
      const { members } = await roomsApi.members(roomId)
      setMembers(members)
    } catch {
      toast.push('Mitglieder konnten nicht geladen werden', 'error')
      setMembers([])
    }
  }

  async function remove(userId: string) {
    setRemoving(userId)
    try {
      await roomsApi.removeMember(roomId, userId)
      toast.push('Mitglied entfernt', 'success')
      await load()
    } catch {
      toast.push('Entfernen fehlgeschlagen', 'error')
    } finally {
      setRemoving(null)
      setConfirmId(null)
    }
  }

  return (
    <aside className="members-panel">
      <header className="members-head">
        <strong>Mitglieder</strong>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Mitgliederliste schließen">
          ✕
        </Button>
      </header>
      <div className="members-body">
        {members === null ? (
          <Spinner />
        ) : members.length === 0 ? (
          <p className="muted">Noch keine Mitglieder.</p>
        ) : (
          <ul className="members-list">
            {members.map((m) => (
              <li key={m.userId} className="member-row">
                <div className="member-info">
                  <div className="member-name">
                    {m.displayName}
                    {m.isOwner ? <span className="tag tag-accent">Inhaber</span> : null}
                  </div>
                  <div className="member-email">{m.email}</div>
                </div>
                {!m.isOwner ? (
                  confirmId === m.userId ? (
                    <div className="member-confirm">
                      <span className="muted">Entfernen?</span>
                      <Button
                        size="sm"
                        variant="danger"
                        loading={removing === m.userId}
                        onClick={() => void remove(m.userId)}
                      >
                        Ja
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                        Nein
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setConfirmId(m.userId)}
                    >
                      Entfernen
                    </Button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  )
}
